/**
 * Norwalt Form - Storefront Analytics, Incomplete Lead Recovery & Submission Engine
 * Automatically tracks form views, captures partial/abandoned leads before exit,
 * and transmits verified form inquiries to Norwalt Form App (PostgreSQL + Shopify Sync + Resend).
 */
(function () {
  'use strict';

  const API_BASE = 'https://norwalt-form-pro.vercel.app';
  const SHOP_DOMAIN = (window.Shopify && window.Shopify.shop) ? window.Shopify.shop : 'norwalt.myshopify.com';

  // 1. Session & Interaction Identifier
  function getSessionId() {
    let sid = sessionStorage.getItem('_norwalt_form_session_id');
    if (!sid) {
      sid = 'norwalt_sess_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9);
      sessionStorage.setItem('_norwalt_form_session_id', sid);
    }
    return sid;
  }

  const sessionId = getSessionId();

  // 2. Helper: Extract all current form values from any container or form
  function extractFormData(container) {
    if (!container) return {};
    const data = {};
    const elements = container.querySelectorAll('input, select, textarea');

    elements.forEach(function (el) {
      const name = el.name || el.id;
      if (!name || el.type === 'submit' || el.type === 'button' || el.type === 'password' || (el.type === 'hidden' && name.startsWith('_'))) {
        return;
      }

      if (el.type === 'checkbox') {
        if (el.checked) {
          data[name] = data[name] ? data[name] + ', ' + (el.value || 'true') : (el.value || 'true');
        }
      } else if (el.type === 'radio') {
        if (el.checked) {
          data[name] = el.value;
        }
      } else if (el.value && el.value.trim() !== '') {
        data[name] = el.value.trim();
      }
    });

    return data;
  }

  // 3. Helper: Extract contact channels
  function extractContactChannels(data) {
    let email = '';
    let phone = '';
    let name = '';

    for (let key in data) {
      const lowerKey = key.toLowerCase();
      const val = String(data[key]).trim();

      if (!email && (lowerKey.includes('email') || lowerKey.includes('mail') || val.includes('@'))) {
        if (val.includes('@') && val.length > 4) {
          email = val;
        }
      }

      if (!phone && (lowerKey.includes('phone') || lowerKey.includes('tel') || lowerKey.includes('mobiel'))) {
        const digits = val.replace(/[^0-9]/g, '');
        if (digits.length >= 7) {
          phone = val;
        }
      }

      if (!name && (lowerKey.includes('name') || lowerKey.includes('naam') || lowerKey === 'fullname')) {
        name = val;
      }
    }

    return { email: email, phone: phone, name: name };
  }

  // 4. Helper: Determine Form Name
  function resolveFormName(formEl, container) {
    if (container && container.dataset && container.dataset.formName) {
      return container.dataset.formName.trim();
    }
    if (formEl && formEl.dataset && formEl.dataset.formName) {
      return formEl.dataset.formName.trim();
    }
    const formId = (formEl || container).id;
    if (formId === 'ctForm') return 'Contact Formulier';
    if (formId === 'adForm') return 'Productadvies Formulier';
    if (formId === 'leadForm') return 'Offerte Aanvraag Modal';
    if (formId === 'savForm') return 'Besparingswizard Aanvraag';
    if (formId === 'b2bRegForm') return 'Zakelijke Aanvraag B2B';

    const formNameInput = (formEl || container).querySelector('input[name="form_name"], input[name="formName"]');
    if (formNameInput && formNameInput.value) {
      return formNameInput.value.trim();
    }
    const titleEl = (container || formEl).querySelector('.form-title, .modal-title, h1, h2, h3');
    if (titleEl && titleEl.textContent && titleEl.textContent.trim()) {
      const text = titleEl.textContent.trim();
      if (text.length < 60) return text;
    }
    return document.title.split('|')[0].trim() || 'Algemeen Formulier';
  }

  // 5. Track Form View (Once per visitor session per form)
  function trackFormView(formName) {
    if (!formName) return;
    const viewKey = '_norwalt_view_tracked_' + formName;
    if (sessionStorage.getItem(viewKey)) {
      return;
    }

    const payload = {
      shop: SHOP_DOMAIN,
      form_name: formName
    };

    fetch(API_BASE + '/api/forms/track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true
    }).then(function () {
      sessionStorage.setItem(viewKey, '1');
    }).catch(function (err) {
      console.warn('[Norwalt Forms] View tracking skipped:', err);
    });
  }

  // 6. Capture Incomplete / Abandoned Form Data
  const debounceTimers = {};

  function sendIncompleteLead(formName, data, status, isBeacon) {
    if (!formName) return;

    if (sessionStorage.getItem('_norwalt_submitted_' + formName)) {
      return;
    }

    const contact = extractContactChannels(data);
    const hasEmail = contact.email && contact.email.includes('@');
    const hasPhone = contact.phone && contact.phone.replace(/[^0-9]/g, '').length >= 7;

    if (!hasEmail && !hasPhone) {
      return;
    }

    const payload = {
      shop: SHOP_DOMAIN,
      session_id: sessionId,
      form_name: formName,
      page_url: window.location.href,
      page_title: document.title,
      email: contact.email || null,
      phone: contact.phone || null,
      name: contact.name || null,
      form_data: data,
      status: status || 'incomplete'
    };

    const payloadStr = JSON.stringify(payload);

    if (isBeacon && navigator.sendBeacon) {
      const blob = new Blob([payloadStr], { type: 'application/json' });
      navigator.sendBeacon(API_BASE + '/api/forms/incomplete', blob);
    } else {
      fetch(API_BASE + '/api/forms/incomplete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payloadStr,
        keepalive: true
      }).catch(function (e) {
        console.warn('[Norwalt Forms] Incomplete capture skipped:', e);
      });
    }
  }

  function queueIncompleteLead(formName, container, status) {
    if (debounceTimers[formName]) {
      clearTimeout(debounceTimers[formName]);
    }

    debounceTimers[formName] = setTimeout(function () {
      const data = extractFormData(container);
      sendIncompleteLead(formName, data, status || 'incomplete', false);
    }, 1200);
  }

  // 7. Core Submit Method
  async function submitForm(formName, data) {
    const payload = Object.assign({
      shop: SHOP_DOMAIN,
      session_id: sessionId,
      form_name: formName,
      page_url: window.location.href,
      page_title: document.title
    }, data);

    const response = await fetch(API_BASE + '/api/submit', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    const result = await response.json();
    if (result && result.success) {
      sessionStorage.setItem('_norwalt_submitted_' + formName, '1');
    }
    return result;
  }

  // 8. Initialize Form Observers
  function initFormTracking() {
    const containers = document.querySelectorAll(
      '[data-form-name], #ctForm, #adForm, #leadForm, #savForm, #b2bRegForm'
    );

    const trackedNames = new Set();

    containers.forEach(function (container) {
      const formEl = container.tagName === 'FORM' ? container : container.querySelector('form');
      const targetContainer = formEl || container;
      const formName = resolveFormName(formEl, container);

      if (!formName || trackedNames.has(formName)) {
        return;
      }
      trackedNames.add(formName);

      // Track View
      trackFormView(formName);

      // Listen for field inputs & blurs
      targetContainer.addEventListener('input', function (e) {
        if (e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT' || e.target.tagName === 'TEXTAREA')) {
          queueIncompleteLead(formName, targetContainer, 'incomplete');
        }
      });

      targetContainer.addEventListener('blur', function (e) {
        if (e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT' || e.target.tagName === 'TEXTAREA')) {
          const data = extractFormData(targetContainer);
          sendIncompleteLead(formName, data, 'incomplete', false);
        }
      }, true);
    });

    // Page exit abandonment
    const handleExit = function () {
      trackedNames.forEach(function (formName) {
        if (!sessionStorage.getItem('_norwalt_submitted_' + formName)) {
          const matching = document.querySelector('[data-form-name="' + formName + '"]') ||
                           document.querySelector('#ctForm, #adForm, #leadForm, #savForm');
          if (matching) {
            const data = extractFormData(matching);
            sendIncompleteLead(formName, data, 'abandoned', true);
          }
        }
      });
    };

    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') {
        handleExit();
      }
    });

    window.addEventListener('beforeunload', handleExit);
  }

  // Export Global Helper
  window.NORWALT_FORMS = {
    API_BASE: API_BASE,
    getSessionId: getSessionId,
    trackView: trackFormView,
    submit: submitForm,
    captureIncomplete: function (formName, data, status) {
      sendIncompleteLead(formName, data, status, false);
    },
    markCompleted: function (formName) {
      if (formName) {
        sessionStorage.setItem('_norwalt_submitted_' + formName, '1');
      }
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFormTracking);
  } else {
    initFormTracking();
  }
})();
